/**
 * CardDetailPage.jsx
 *
 * Détail d'une carte avec :
 * - Image grande
 * - Infos et effets
 * - Graphique des prix
 * - Historique des offres
 * - Design Ant Design
 */

import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Row, Col, Card, Button, Statistic, Space, Empty, Spin, Divider, Tag } from "antd";
import { ArrowLeftOutlined, HeartOutlined, HeartFilled } from "@ant-design/icons";
import { getCardById, addFavorite, removeFavorite } from "../api/api";
import PriceChart from "../components/Charts/PriceChart";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { formatDateTime, formatPrice, getStatusColor } from "../utils/formatters";

/**
 * CardDetailPage - Détail d'une carte
 */
export default function CardDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { token } = useAuth();
    const { isDarkMode } = useTheme();

    const [card, setCard] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isFavorite, setIsFavorite] = useState(false);

    useEffect(() => {
        const fetchCard = async () => {
            try {
                setLoading(true);
                const res = await getCardById(id);
                const cardData = res.data.card;
                setCard(cardData);
                setIsFavorite(cardData.favorite_status === "FAVORITE");
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        fetchCard();
    }, [id]);

    const handleFavorite = async () => {
        if (!token) {
            alert("Connectez-vous pour ajouter aux favoris !");
            return;
        }

        try {
            if (isFavorite) {
                await removeFavorite(card.id);
            } else {
                await addFavorite(card.id);
            }
            setIsFavorite(!isFavorite);
        } catch (err) {
            console.error(err);
            alert("Erreur favoris");
        }
    };

    if (loading) {
        return (
            <div style={{ textAlign: "center", padding: "48px" }}>
                <Spin />
            </div>
        );
    }

    if (!card) {
        return <Empty description="Carte non trouvée" style={{ padding: "48px" }} />;
    }

    const offers = card.offers || card.all_offers || [];

    return (
        <div style={{ padding: "24px", background: isDarkMode ? "#000000" : "#fafafa", minHeight: "100vh" }}>
            {/* Bouton retour */}
            <Button
                type="text"
                icon={<ArrowLeftOutlined />}
                onClick={() => navigate(-1)}
                style={{ marginBottom: "16px" }}
            >
                Retour
            </Button>

            <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
                {/* Header avec image et infos */}
                <Card
                    style={{
                        background: isDarkMode ? "#1f1f1f" : "#ffffff",
                        marginBottom: "24px",
                    }}
                >
                    <Row gutter={[32, 32]}>
                        {/* Image */}
                        <Col xs={24} sm={24} md={8}>
                            <img
                                src={card.image_path}
                                alt={card.name}
                                style={{
                                    width: "100%",
                                    borderRadius: "8px",
                                }}
                            />
                        </Col>

                        {/* Infos */}
                        <Col xs={24} sm={24} md={16}>
                            {/* Titre et favoris */}
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "flex-start",
                                    gap: "12px",
                                    marginBottom: "24px",
                                }}
                            >
                                <div style={{ flex: 1 }}>
                                    <h1 style={{ margin: "0 0 8px", fontSize: "32px" }}>
                                        {card.name}
                                    </h1>
                                </div>
                                <Button
                                    type="text"
                                    icon={isFavorite ? <HeartFilled /> : <HeartOutlined />}
                                    onClick={handleFavorite}
                                    danger={isFavorite}
                                    size="large"
                                />
                            </div>

                            {/* Effets */}
                            <div style={{ marginBottom: "24px" }}>
                                <p style={{ fontSize: "16px", fontStyle: "italic" }}>
                                    {card.main_effect}
                                </p>
                                {card.echo_effect && (
                                    <p style={{ fontSize: "14px", opacity: 0.7, marginTop: "8px" }}>
                                        <strong>Echo :</strong> {card.echo_effect}
                                    </p>
                                )}
                            </div>

                            {/* Attributs */}
                            <Divider />
                            <h3 style={{ marginBottom: "16px" }}>Attributs</h3>
                            <Row gutter={[16, 16]}>

                                <Col xs={12} sm={8}>
                                    <Statistic
                                        title="Forêt"
                                        value={card.elements.forest}
                                    />
                                </Col>
                                <Col xs={12} sm={8}>
                                    <Statistic
                                        title="Eau"
                                        value={card.elements.water}
                                    />
                                </Col>
                                <Col xs={12} sm={8}>
                                    <Statistic
                                        title="Montagne"
                                        value={card.elements.mountain}
                                    />
                                </Col>
                                <Col xs={12} sm={8}>
                                    <Statistic
                                        title="Coût main"
                                        value={card.elements.main_cost}
                                    />
                                </Col>
                                <Col xs={12} sm={8}>
                                    <Statistic
                                        title="Coût de réserve"
                                        value={card.elements.recoll_cost}
                                    />
                                </Col>
                            </Row>

                            {/* Lien externe */}
                            <div style={{ marginTop: "24px" }}>
                                <Button
                                    type="primary"
                                    href={`https://www.altered.gg/fr-fr/cards/${card.reference}/offers`}
                                    target="_blank"
                                    rel="noreferrer"
                                    size="large"
                                >
                                    Voir sur Altered.gg
                                </Button>
                            </div>
                        </Col>
                    </Row>
                </Card>

                {/* Graphique et historique */}
                {offers.length > 0 && (
                    <Row gutter={[24, 24]}>
                        <Col xs={24} md={16}>
                            <PriceChart offers={offers} />
                        </Col>

                        <Col xs={24} md={8}>
                            <Card
                                title="Historique des offres"
                                style={{ background: isDarkMode ? "#1f1f1f" : "#ffffff" }}
                            >
                                <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                                    {offers.map((o, i) => (
                                        <div
                                            key={i}
                                            style={{
                                                padding: "8px 0",
                                                borderBottom: `1px solid ${isDarkMode ? "#434343" : "#f0f0f0"}`,
                                            }}
                                        >
                                            <div
                                                style={{
                                                    display: "flex",
                                                    justifyContent: "space-between",
                                                    alignItems: "center",
                                                    marginBottom: "4px",
                                                }}
                                            >
                                                <span style={{ fontWeight: "600" }}>
                                                    {formatPrice(o.price, o.devise)}
                                                </span>
                                                <Tag color={getStatusColor(o.status)}>
                                                    {o.status === "IN_SELL" ? "En vente" : "Vendu"}
                                                </Tag>
                                            </div>
                                            <span
                                                style={{
                                                    fontSize: "12px",
                                                    opacity: 0.6,
                                                }}
                                            >
                                                {formatDateTime(o.date)}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </Card>
                        </Col>
                    </Row>
                )}
            </div>
        </div>
    );
}